'use client';
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ConciergeBell, Stethoscope, FlaskConical, Microscope, Pill, Receipt,
  AlertCircle, BedDouble, HeartPulse, HelpCircle, ArrowRight, ArrowLeft,
  ArrowUp, ArrowDown, MapPin, Phone, MessageCircle, QrCode, Info, CheckCircle
} from 'lucide-react';
import TopNav from '@/components/layout/TopNav';
import { getAvailableDestinations } from '@/data/wayfinding';
import type { WayfindingDestination } from '@/data/wayfinding';
import { useSelectedCentre } from '@/context/SelectedCentreContext';
import { hospitalConfig } from '@/data/hospitalConfig';
import { trackEvent } from '@/lib/analytics';

const iconMap: Record<string, React.ElementType> = {
  ConciergeBell, Stethoscope, FlaskConical, Microscope, Pill, Receipt,
  AlertCircle, BedDouble, HeartPulse, HelpCircle,
};

const directionIconMap: Record<string, React.ElementType> = {
  straight: ArrowUp,
  left: ArrowLeft,
  right: ArrowRight,
  up: ArrowUp,
  down: ArrowDown,
};

type WayfindingStep = 'select' | 'directions';

export default function AtAllIsWellContent() {
  const { selectedCentre } = useSelectedCentre();
  const [step, setStep] = useState<WayfindingStep>('select');
  const [selectedDestination, setSelectedDestination] = useState<WayfindingDestination | null>(null);
  const [currentDirectionStep, setCurrentDirectionStep] = useState(0);

  const centreType = selectedCentre?.type;
  const availableDestinations = getAvailableDestinations(centreType);

  const handleDestinationSelect = (dest: WayfindingDestination) => {
    setSelectedDestination(dest);
    setCurrentDirectionStep(0);
    setStep('directions');
    trackEvent({ event: 'journey_started', source: `wayfinding-${dest.id}` });
  };

  const handleBack = () => {
    setStep('select');
    setSelectedDestination(null);
    setCurrentDirectionStep(0);
  };

  const handleNextStep = () => {
    if (selectedDestination?.directions && currentDirectionStep < selectedDestination.directions.length - 1) {
      setCurrentDirectionStep((prev) => prev + 1);
    }
  };

  const handlePrevStep = () => {
    if (currentDirectionStep > 0) {
      setCurrentDirectionStep((prev) => prev - 1);
    }
  };

  return (
    <main className="journey-bg min-h-screen flex flex-col pb-24" role="main" aria-label="I'm Already at All Is Well — Wayfinding">
      <TopNav showBackButton backHref="/digital-reception-screen" backLabel="Back" />

      <div className="flex-1 flex flex-col items-center pt-24 px-4 md:px-8">
        <div className="w-full max-w-2xl">

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45 }}
            className="mb-8"
          >
            <p className="section-label mb-2">I&apos;M ALREADY AT ALL IS WELL</p>
            <h1 className="text-display-md text-white font-bold leading-tight mb-2">
              WELCOME.<br />
              <span className="gold-gradient-text">WHERE DO YOU</span><br />
              <span className="text-white">NEED TO GO?</span>
            </h1>
            {selectedCentre ? (
              <div className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-primary/10 border border-primary/20">
                <MapPin size={11} className="text-primary" />
                <span className="text-xs text-primary font-medium">{selectedCentre.name}</span>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground mt-2">
                Select your centre from the header for a more accurate experience.
              </p>
            )}
          </motion.div>

          <AnimatePresence mode="wait">
            {/* STEP: Destination selector */}
            {step === 'select' && (
              <motion.div
                key="select"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.35 }}
              >
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {availableDestinations.map((dest, idx) => {
                    const IconComp = iconMap[dest.icon] || HelpCircle;
                    const isEmergency = dest.id === 'emergency';
                    return (
                      <motion.button
                        key={dest.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.05 }}
                        whileHover={{ y: -3, scale: 1.02 }}
                        whileTap={{ scale: 0.97 }}
                        onClick={() => handleDestinationSelect(dest)}
                        className={`premium-concern-card ${isEmergency ? 'border-red-900/50 hover:border-red-500/50' : ''}`}
                        aria-label={dest.label}
                      >
                        <div className={`concern-icon-wrap ${isEmergency ? 'bg-red-950/50 border-red-900/50' : ''}`}>
                          <IconComp
                            size={20}
                            className={isEmergency ? 'text-red-400' : 'text-primary'}
                            aria-hidden="true"
                          />
                        </div>
                        <span className={`text-xs font-bold tracking-wide leading-snug text-center ${isEmergency ? 'text-red-300' : 'text-foreground'}`}>
                          {dest.label}
                        </span>
                        <span className="text-xs text-muted-foreground text-center leading-snug hidden sm:block">
                          {dest.description}
                        </span>
                      </motion.button>
                    );
                  })}
                </div>

                {/* QR future architecture note */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="mt-8 p-4 rounded-xl bg-card/40 border border-border/40 flex items-start gap-3"
                >
                  <QrCode size={16} className="text-muted-foreground flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-xs font-semibold text-muted-foreground tracking-wide mb-0.5">SCAN TO NAVIGATE</p>
                    <p className="text-xs text-muted-foreground/70 leading-relaxed">
                      QR-based indoor navigation is coming soon. Look for All Is Well QR codes at reception and key locations.
                    </p>
                  </div>
                </motion.div>
              </motion.div>
            )}

            {/* STEP: Directions */}
            {step === 'directions' && selectedDestination && (
              <motion.div
                key="directions"
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                transition={{ duration: 0.35 }}
              >
                <button
                  onClick={handleBack}
                  className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors text-sm mb-6"
                  aria-label="Go back to destination selection"
                >
                  <ArrowLeft size={14} />
                  <span className="text-xs tracking-widest uppercase">Choose Different Destination</span>
                </button>

                {/* Destination header */}
                <div className="bg-card border border-border rounded-2xl p-6 mb-4">
                  <div className="flex items-center gap-3 mb-4">
                    {(() => {
                      const IconComp = iconMap[selectedDestination.icon] || HelpCircle;
                      return (
                        <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center">
                          <IconComp size={22} className="text-primary" aria-hidden="true" />
                        </div>
                      );
                    })()}
                    <div>
                      <h2 className="text-xl font-bold text-white">{selectedDestination.label}</h2>
                      <p className="text-xs text-muted-foreground">{selectedDestination.description}</p>
                    </div>
                  </div>

                  {/* Direction steps */}
                  {selectedDestination.directions && selectedDestination.directions.length > 0 ? (
                    <div>
                      <p className="text-xs text-muted-foreground tracking-widest uppercase mb-4">YOUR ROUTE</p>

                      {/* Route visualization */}
                      <div className="space-y-2 mb-6">
                        {selectedDestination.directions.map((dirStep, idx) => {
                          const DirIcon = dirStep.direction ? directionIconMap[dirStep.direction] : ArrowUp;
                          const isActive = idx === currentDirectionStep;
                          const isCompleted = idx < currentDirectionStep;
                          return (
                            <motion.div
                              key={dirStep.step}
                              initial={{ opacity: 0, x: -8 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: idx * 0.08 }}
                              className={`flex items-start gap-3 p-3 rounded-xl transition-all duration-300 ${
                                isActive
                                  ? 'bg-primary/10 border border-primary/30'
                                  : isCompleted
                                  ? 'bg-emerald-950/30 border border-emerald-900/30 opacity-60' :'bg-muted/20 border border-border/30 opacity-40'
                              }`}
                            >
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                isActive ? 'bg-primary text-primary-foreground' :
                                isCompleted ? 'bg-emerald-700 text-white' : 'bg-muted text-muted-foreground'
                              }`}>
                                {isCompleted ? (
                                  <CheckCircle size={14} />
                                ) : (
                                  <DirIcon size={14} />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className={`text-sm font-medium ${isActive ? 'text-white' : 'text-muted-foreground'}`}>
                                  {dirStep.instruction}
                                </p>
                                {dirStep.landmark && (
                                  <p className="text-xs text-muted-foreground/70 mt-0.5">{dirStep.landmark}</p>
                                )}
                              </div>
                            </motion.div>
                          );
                        })}
                      </div>

                      {/* Navigation controls */}
                      <div className="flex gap-3">
                        <button
                          onClick={handlePrevStep}
                          disabled={currentDirectionStep === 0}
                          className="flex-1 btn-ghost-white text-xs py-2.5 disabled:opacity-30 disabled:cursor-not-allowed"
                          aria-label="Previous step"
                        >
                          ← PREV
                        </button>
                        {currentDirectionStep < (selectedDestination.directions.length - 1) ? (
                          <button
                            onClick={handleNextStep}
                            className="flex-1 btn-primary text-xs py-2.5"
                            aria-label="Next step"
                          >
                            NEXT STEP →
                          </button>
                        ) : (
                          <div className="flex-1 flex items-center justify-center gap-2 bg-emerald-900/30 border border-emerald-700/40 rounded-lg text-emerald-400 text-xs font-semibold tracking-wide">
                            <CheckCircle size={13} />
                            YOU&apos;VE ARRIVED
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      Please ask at Reception for directions to {selectedDestination.label}.
                    </p>
                  )}
                </div>

                {/* Emergency override */}
                {selectedDestination.id === 'emergency' && (
                  <div className="p-4 rounded-xl bg-red-950/40 border border-red-900/50">
                    <p className="text-sm font-bold text-red-300 mb-3">
                      If this is a medical emergency, call immediately:
                    </p>
                    <a
                      href={hospitalConfig.emergencyTel}
                      className="btn-emergency w-full text-sm"
                      aria-label="Call emergency now"
                    >
                      <AlertCircle size={16} />
                      CALL EMERGENCY NOW
                    </a>
                  </div>
                )}

                {/* Contact options */}
                <div className="grid grid-cols-2 gap-3 mt-4">
                  <a
                    href={hospitalConfig.phoneTel}
                    className="flex items-center justify-center gap-2 btn-outline-gold text-xs px-4 py-3"
                    aria-label="Call All Is Well"
                  >
                    <Phone size={13} />
                    CALL
                  </a>
                  <a
                    href={hospitalConfig.whatsapp}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-2 btn-ghost-white text-xs px-4 py-3"
                    aria-label="WhatsApp All Is Well"
                  >
                    <MessageCircle size={13} />
                    WHATSAPP
                  </a>
                </div>

                {/* Future indoor nav note */}
                <div className="mt-6 p-3 rounded-xl bg-card/30 border border-border/30 flex items-start gap-2">
                  <Info size={12} className="text-muted-foreground/60 flex-shrink-0 mt-0.5" />
                  <p className="text-xs text-muted-foreground/60 leading-relaxed">
                    Detailed floor plans and indoor navigation will be available in a future update. For now, our team at Reception is always happy to guide you.
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </main>
  );
}
